// src/services/queries/dashboards/sectorQueries.ts

import type { QueryFunction } from "@tanstack/react-query";
import { queryKeys } from "@/constants/queryKeys";
import type { SectorDashboardResponse } from "@/types/dashboards/dashboardResponses";
import { TIME } from "@/constants/app";
import { API_BASE_URL } from "@/services/";
import type { CategoryFlat } from "@/types/dashboards/categories";
import { MetricKeys } from "@/constants/metrics";
import type { WindowValue } from "@/types/dashboards/dashboardCurrent";

/**
 * Query function for fetching sector dashboard data
 * This function is pure and can be easily tested
 */
export const fetchSectorData: QueryFunction<SectorDashboardResponse> = async ({
  signal,
}) => {
  const init: RequestInit = {};
  if (signal) init.signal = signal;

  // 1) Fetch flat categories to know which categories to request
  const flatRes = await fetch(`${API_BASE_URL}/categories`, init);
  if (!flatRes.ok) throw new Error(`HTTP error! status: ${flatRes.status}`);
  const categories: CategoryFlat[] = await flatRes.json();

  // 2) Fetch ALL current and ALL changes in two bulk requests
  type CategoryCurrentSnapshot = { category_id: number; metrics: Record<string, number | null> };
  type CategoryChangesSnapshot = { category_id: number; metrics: Record<string, Record<string, WindowValue>> };

  const currentRes = await fetch(`${API_BASE_URL}/categories/current`, init);
  if (!currentRes.ok) throw new Error(`HTTP error! status: ${currentRes.status}`);
  const currentList = (await currentRes.json()) as CategoryCurrentSnapshot[];

  const changesRes = await fetch(`${API_BASE_URL}/categories/current/changes`, init);
  if (!changesRes.ok) throw new Error(`HTTP error! status: ${changesRes.status}`);
  const changesList = (await changesRes.json()) as CategoryChangesSnapshot[];

  const currentById = new Map<number, CategoryCurrentSnapshot>();
  for (const c of currentList) currentById.set(c.category_id, c);
  const changesById = new Map<number, CategoryChangesSnapshot>();
  for (const ch of changesList) changesById.set(ch.category_id, ch);

  // Build results for all categories (parents and leaves)
  const results = categories.map((cat) => {
    const current = currentById.get(cat.category_id);
    const changes = changesById.get(cat.category_id);

    const marketCap = current?.metrics?.[MetricKeys.MarketCap] ?? 0;

    const getChangePct = (win: string): number => {
      const w = changes?.metrics?.[MetricKeys.MarketCap]?.[win] as WindowValue | undefined;
      const v = w?.value;
      return typeof v === "number" ? v * 100 : 0;
    };

    return {
      sector: cat.category_code,
      market_cap: marketCap,
      dominance: 0, // to be filled after total
      change_1d: getChangePct("1d"),
      change_7d: getChangePct("7d"),
      change_30d: getChangePct("30d"),
      change_60d: getChangePct("60d"),
      change_90d: getChangePct("90d"),
      change_6m: getChangePct("180d"),
      change_1y: getChangePct("365d"),
    };
  });

  // 3) Compute dominance based on total market cap
  const totalMcap = results.reduce((sum, r) => sum + (r.market_cap ?? 0), 0);
  const data = results.map((r) => ({
    ...r,
    dominance: totalMcap > 0 ? (r.market_cap / totalMcap) * 100 : 0,
  }));

  const lastUpdated = new Date().toISOString();
  return { data, lastUpdated } as SectorDashboardResponse;
};

/**
 * Query options factory for sector dashboard data
 * Centralizes query configuration
 */
export const sectorQueryOptions = () => ({
  queryKey: queryKeys.dashboard.sector(),
  queryFn: fetchSectorData,
  staleTime: 5 * TIME.MINUTE, // 5 minutes - data considered fresh
  gcTime: 10 * TIME.MINUTE, // 10 minutes - garbage collection time (replaces cacheTime)
  refetchOnWindowFocus: true,
  refetchInterval: 5 * TIME.MINUTE, // Auto-refetch every 5 minutes
  retry: (failureCount: number, error: Error) => {
    // Don't retry on 4xx errors
    if (error.message.includes("4")) {
      return false;
    }
    return failureCount < 3;
  },
});
